import { AppButton } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import type { InquiryResponse } from 'backend/types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { INQUIRY_ASSIGN_STATUSES } from 'shared'
import { useInquiryAssigns } from '../../hooks/useInquiryAssigns'
import { useInquiryEditForm } from '../../hooks/useInquiryEditForm'
import { useInquirySales } from '../SalesTab/hooks/useInquirySales'
import { useCreateInquiryRelatedDivision } from './hooks/useCreateInquiryRelatedDivision'
import { useDeleteInquiryRelatedDivision } from './hooks/useDeleteInquiryRelatedDivision'
import { ProjectOverviewSection } from './sections/ProjectOverviewSection'
import { type AddedRelatedDivision, StaffSection } from './sections/StaffSection'
import { StatusScheduleSection } from './sections/StatusScheduleSection'

type PropertiesTabProps = {
  inquiry: InquiryResponse
  isInquiryCompleted: boolean
  onDirtyChange: (isDirty: boolean) => void
}

export function PropertiesTab({ inquiry, isInquiryCompleted, onDirtyChange }: PropertiesTabProps) {
  const { data: salesData } = useInquirySales(inquiry.id)
  const firstMonthSalesStageId = useMemo(
    () =>
      salesData?.sales.filter((s) => s.enabled).sort((a, b) => a.sales_on.localeCompare(b.sales_on) || a.id - b.id)[0]
        ?.sales_stage_id ?? null,
    [salesData],
  )

  // ステータスが取り下げ以外のアサインがあるかどうか
  const { data: assignsData } = useInquiryAssigns(inquiry.id)
  const hasActiveAssigns =
    assignsData?.inquiry_assigns.some((a) => a.inquiry_assign_status_id !== INQUIRY_ASSIGN_STATUSES.WITHDRAWN) ?? false

  const {
    formValues,
    formErrors,
    hasErrors,
    isDirty: formIsDirty,
    isSaving,
    isPrimaryDivisionLocked,
    isPrimaryDivisionDisabled,
    updateField,
    handleAnoChange,
    resetForm,
    handleSave,
    validate,
  } = useInquiryEditForm(inquiry, hasActiveAssigns)
  const { mutateAsync: createRelatedDivision } = useCreateInquiryRelatedDivision(inquiry.id)
  const { mutateAsync: deleteRelatedDivision } = useDeleteInquiryRelatedDivision(inquiry.id)

  // 関連部門のローカル管理（追加・削除）
  const [addedRelatedDivisions, setAddedRelatedDivisions] = useState<AddedRelatedDivision[]>([])
  const [removedRelatedDivisionIds, setRemovedRelatedDivisionIds] = useState<number[]>([])
  const [isSavingRelatedDivisions, setIsSavingRelatedDivisions] = useState(false)
  const isRelatedDivisionsDirty = addedRelatedDivisions.length > 0 || removedRelatedDivisionIds.length > 0

  const isDirty = formIsDirty || isRelatedDivisionsDirty

  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  const handleReset = useCallback(() => {
    resetForm()
    setAddedRelatedDivisions([])
    setRemovedRelatedDivisionIds([])
  }, [resetForm])

  const handleAddRelatedDivision = useCallback((divisionId: number, name: string) => {
    setAddedRelatedDivisions((prev) => [...prev, { divisionId, name }])
  }, [])

  const handleRemoveExistingRelatedDivision = useCallback((id: number) => {
    setRemovedRelatedDivisionIds((prev) => [...prev, id])
  }, [])

  const handleRemoveAddedRelatedDivision = useCallback((divisionId: number) => {
    setAddedRelatedDivisions((prev) => prev.filter((d) => d.divisionId !== divisionId))
  }, [])

  const isPending = isSaving || isSavingRelatedDivisions

  const handleSaveAll = useCallback(async () => {
    if (!validate()) return
    setIsSavingRelatedDivisions(true)
    try {
      if (removedRelatedDivisionIds.length > 0) {
        await Promise.all(removedRelatedDivisionIds.map((id) => deleteRelatedDivision(id)))
        setRemovedRelatedDivisionIds([])
      }
      if (addedRelatedDivisions.length > 0) {
        await Promise.all(addedRelatedDivisions.map((d) => createRelatedDivision(d.divisionId)))
        setAddedRelatedDivisions([])
      }
      if (formIsDirty) {
        await handleSave()
      }
    } catch {
    } finally {
      setIsSavingRelatedDivisions(false)
    }
  }, [
    validate,
    removedRelatedDivisionIds,
    addedRelatedDivisions,
    formIsDirty,
    deleteRelatedDivision,
    createRelatedDivision,
    handleSave,
  ])

  return (
    <div className='flex h-full flex-col'>
      <div className='flex-1 space-y-6 overflow-auto p-4'>
        <ProjectOverviewSection
          salesforceCustomerId={inquiry.salesforce_customer_id}
          salesforceDealId={inquiry.deal_id}
          initialSalesforceProjectId={inquiry.salesforce_project_id}
          formValues={formValues}
          formErrors={formErrors}
          onFieldChange={updateField}
          onAnoChange={handleAnoChange}
          isInquiryCompleted={isInquiryCompleted}
        />
        <StatusScheduleSection
          inquiry={inquiry}
          firstMonthSalesStageId={firstMonthSalesStageId}
          formValues={formValues}
          formErrors={formErrors}
          onFieldChange={updateField}
          isInquiryCompleted={isInquiryCompleted}
        />
        <StaffSection
          inquiry={inquiry}
          formValues={formValues}
          onFieldChange={updateField}
          addedRelatedDivisions={addedRelatedDivisions}
          removedRelatedDivisionIds={removedRelatedDivisionIds}
          onAddRelatedDivision={handleAddRelatedDivision}
          onRemoveExistingRelatedDivision={handleRemoveExistingRelatedDivision}
          onRemoveAddedRelatedDivision={handleRemoveAddedRelatedDivision}
          isPrimaryDivisionLocked={isPrimaryDivisionLocked}
          isPrimaryDivisionDisabled={isPrimaryDivisionDisabled}
          isInquiryCompleted={isInquiryCompleted}
        />
      </div>

      {/* フッター */}
      <div className='flex items-center justify-end gap-2 border-t px-4 py-3'>
        <AppButton variant='outline' disabled={!isDirty || isPending} onClick={handleReset}>
          キャンセル
        </AppButton>
        <AppButton variant='save' disabled={!isDirty || isPending || hasErrors} onClick={handleSaveAll}>
          {isPending ? '保存中...' : '保存'}
        </AppButton>
      </div>
    </div>
  )
}
